import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase-auth'
import { useAuth } from '../../contexts/AuthContextLazy'
import { 
  ArrowLeftIcon,
  BuildingOfficeIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline'
import { showSuccess, showError } from '../../utils/toast'
import '../../styles/portal-ui-polish.css'

export const NewClient: React.FC = () => {
  const navigate = useNavigate()
  const { hasRole, userProfile } = useAuth()
  const [loading, setLoading] = useState(false)
  const [advisors, setAdvisors] = useState<Array<{ id: string; full_name: string }>>([])
  
  // Form state
  const [formData, setFormData] = useState({
    // Client info
    name: '',
    type: 'individual' as 'individual' | 'organization',
    taxId: '',
    
    // Contact info
    primaryEmail: '',
    primaryPhone: '',
    
    // Additional info
    assignedAdvisorId: '',
    status: 'active' as 'active' | 'inactive'
  })

  useEffect(() => {
    fetchAdvisors()
  }, [])

  const fetchAdvisors = async () => {
    try {
      const { data, error } = await supabase
        .from('platform_users')
        .select('id, full_name')
        .in('role', ['member', 'admin', 'owner'])
        .order('full_name')

      if (error) throw error
      setAdvisors(data || [])
      
      // Auto-select current user as advisor if they're an advisor
      if (userProfile && ['member', 'admin', 'owner'].includes(userProfile.role)) {
        setFormData(prev => ({ ...prev, assignedAdvisorId: userProfile.id }))
      }
    } catch (error) {
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Error fetching advisors:', error);
        }

      }
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      // Get the platform account ID (should be only one for PCM)
      const { data: platformAccount, error: accountError } = await supabase
        .from('platform_accounts')
        .select('id')
        .single()

      if (accountError || !platformAccount) {
        throw new Error('Platform account not found. Please ensure database is properly set up.')
      }

      // Create the party (client)
      const { data: insertResult, error: partyError } = await supabase
        .from('parties')
        .insert({
          platform_account_id: platformAccount.id,
          name: formData.name,
          type: formData.type,
          tax_id: formData.taxId || null,
          primary_email: formData.primaryEmail || null,
          primary_phone: formData.primaryPhone || null,
          assigned_advisor_id: formData.assignedAdvisorId || null,
          status: formData.status
        })

      if (partyError) throw partyError

      showSuccess('Client created successfully!')
      navigate('/portal/clients')
      
    } catch (err: any) {
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Error creating client:', err);
        }

      }
      showError(err.message || 'Failed to create client')
      setLoading(false)
    }
  }

  if (!hasRole(['super_admin', 'pcm_staff'])) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">You don't have permission to view this page.</p>
      </div>
    )
  }

  return (
    <div className="portal-page-bg min-h-screen">
      <div className="py-4 sm:py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Mobile-optimized Header */}
          <div className="mb-4 sm:mb-6">
            <button
              onClick={() => navigate('/portal/clients')}
              className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 transition-colors mb-3"
            >
              <ArrowLeftIcon className="mr-1.5 h-4 w-4" />
              <span className="sr-only sm:not-sr-only">Back to Clients</span>
            </button>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Add New Client</h1>
            <p className="mt-1 text-xs sm:text-sm text-gray-600">Create a new client profile</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Client Information */}
            <div className="portal-card shadow-sm">
              <div className="px-4 py-4 sm:p-6">
                <div className="flex items-start sm:items-center mb-4 sm:mb-6">
                  <div className="flex-shrink-0">
                    <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg bg-amber-100 flex items-center justify-center">
                      <UserIcon className="h-5 w-5 sm:h-6 sm:w-6 text-amber-600" />
                    </div>
                  </div>
                  <div className="ml-3 sm:ml-4">
                    <h3 className="text-base sm:text-lg font-medium text-gray-900">
                      Client Information
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-500 hidden sm:block">Basic details about the client</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      id="name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="portal-input"
                      placeholder="John Doe or Acme Corporation"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-1">
                        Type <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="type"
                        name="type"
                        value={formData.type}
                        onChange={(e) => setFormData({ ...formData, type: e.target.value as 'individual' | 'organization' })}
                        className="portal-input"
                      >
                        <option value="individual">Individual</option>
                        <option value="organization">Organization</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                        Status
                      </label>
                      <select
                        id="status"
                        name="status"
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value as 'active' | 'inactive' })}
                        className="portal-input"
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="taxId" className="block text-sm font-medium text-gray-700 mb-1">
                      Tax ID / SIN
                    </label>
                    <input
                      type="text"
                      name="taxId"
                      id="taxId"
                      value={formData.taxId}
                      onChange={(e) => setFormData({ ...formData, taxId: e.target.value })}
                      className="portal-input"
                      placeholder="Optional"
                    />
                  </div>

                  <div>
                    <label htmlFor="assignedAdvisorId" className="block text-sm font-medium text-gray-700 mb-1">
                      Assigned Advisor
                    </label>
                    <select
                      id="assignedAdvisorId"
                      name="assignedAdvisorId"
                      value={formData.assignedAdvisorId}
                      onChange={(e) => setFormData({ ...formData, assignedAdvisorId: e.target.value })}
                      className="portal-input"
                    >
                      <option value="">Select an advisor</option>
                      {advisors.map((advisor) => (
                        <option key={advisor.id} value={advisor.id}>
                          {advisor.full_name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="portal-card shadow-sm">
              <div className="px-4 py-4 sm:p-6">
                <div className="flex items-start sm:items-center mb-4 sm:mb-6">
                  <div className="flex-shrink-0">
                    <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                      <EnvelopeIcon className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
                    </div>
                  </div>
                  <div className="ml-3 sm:ml-4">
                    <h3 className="text-base sm:text-lg font-medium text-gray-900">
                      Contact Information
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-500 hidden sm:block">How to reach the client</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="primaryEmail" className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="email"
                        name="primaryEmail"
                        id="primaryEmail"
                        value={formData.primaryEmail}
                        onChange={(e) => setFormData({ ...formData, primaryEmail: e.target.value })}
                        className="pl-10 portal-input"
                        placeholder="client@example.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="primaryPhone" className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <PhoneIcon className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="tel"
                        name="primaryPhone"
                        id="primaryPhone"
                        value={formData.primaryPhone}
                        onChange={(e) => setFormData({ ...formData, primaryPhone: e.target.value })}
                        className="pl-10 portal-input"
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 -mx-4 px-4 py-3 sm:relative sm:bg-transparent sm:border-0 sm:mx-0 sm:px-0 sm:py-0">
              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
                <button
                  type="button"
                  onClick={() => navigate('/portal/clients')}
                  className="portal-button-secondary w-full sm:w-auto"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || !formData.name}
                  className="portal-button-primary w-full sm:w-auto flex justify-center items-center"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Creating...
                    </>
                  ) : (
                    'Create Client'
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}